// P11 — Board do rito (visualização por estágio + pendências). Consome
// GET /api/comite/rito-board (só metadados/contagens, zero decifragem/teor).
// Content-free: categoria + estado + prazo (âncoras) + pendências agregadas.
import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Card, categoriaLabel } from '@aions/ui-primitives';
import type { RitoBoard, RitoCaseCard } from '@aions/core';
import { apiFetch } from '../lib/auth-client';

// Esta tela mantinha a SEGUNDA cópia da lista de categorias, fora do i18n, e a
// cópia já tinha divergido: ficou com 11 entradas, sem `violacao_conduta_etica`
// (a 12ª, confirmada pela compliance em 27/07). Com o fallback `?? c` isso não
// aparecia como erro — imprimia o slug cru na tela do comitê. A cura é não ter
// cópia: `categoriaLabel` lê o mesmo `categorias.*` que as demais telas usam.

const isoDate = (ms: number | null): string =>
  ms === null ? '—' : new Date(ms).toISOString().slice(0, 10);

/**
 * Data e hora — decisão do operador (2026-08-13) para o "aberto em".
 *
 * Separado do `isoDate` de propósito: os PRAZOS (`prazoRecursalFimEpochMs`) são
 * contados em dias úteis pelo `IT-CMP-002` §6, sem hora — pôr hora num prazo daria
 * precisão que a norma não tem e sugeriria um vencimento por horário que não existe.
 * O "aberto em" é outra coisa: é o instante do ato, e é o que foi pedido em data e
 * hora. Um formatador para os dois colapsaria a distinção.
 */
const dataHora = (ms: number | null): string =>
  ms === null
    ? '—'
    : new Date(ms).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });

/** Tom do badge de pendência: bloqueio (âmbar) vs em-curso (neutro). */
function pendTone(code: string): { bg: string; fg: string } {
  const bloqueio = new Set([
    'triagem_nao_classificada',
    'oitiva_sem_ata',
    'quorum_incompleto',
    'decisao_pendente',
    'retorno_comite_pendente',
    'oitiva_pendente_registro',
  ]);
  return bloqueio.has(code)
    ? { bg: 'rgba(234, 179, 8, 0.18)', fg: '#eab308' }
    : { bg: 'rgba(148, 163, 184, 0.18)', fg: '#94a3b8' };
}

function CaseCardView({
  card,
  onOpen,
}: {
  card: RitoCaseCard;
  onOpen: (id: string) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const prazo =
    card.prazoRecursalFimEpochMs !== null
      ? `Recursal até ${isoDate(card.prazoRecursalFimEpochMs)}`
      : card.classifiedAt !== null
        ? `Classificado ${dataHora(card.classifiedAt)}`
        : card.openedAt !== null
          ? `Aberto ${dataHora(card.openedAt)}`
          : '—';
  return (
    <button
      type="button"
      onClick={() => onOpen(card.caseId)}
      data-testid={`rito-card-${card.caseId}`}
      style={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        border: '1px solid var(--border, #2a2a3a)',
        borderRadius: 8,
        padding: 10,
        marginBottom: 8,
        background: 'var(--surface, #16161f)',
        color: 'inherit',
        cursor: 'pointer',
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 600 }}>{categoriaLabel(t, card.categoria)}</div>
      <div style={{ fontSize: 11, opacity: 0.6, fontFamily: 'monospace' }}>{card.caseId}</div>
      <div style={{ fontSize: 11, opacity: 0.7, marginTop: 2 }}>{prazo}</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
        {card.pendencias.map((pend) => {
          const tone = pendTone(pend.code);
          return (
            <span
              key={pend.code}
              data-testid={`rito-pend-${pend.code}`}
              style={{
                fontSize: 11,
                padding: '2px 6px',
                borderRadius: 6,
                background: tone.bg,
                color: tone.fg,
              }}
            >
              {pend.label}
              {pend.detail ? ` · ${pend.detail}` : ''}
            </span>
          );
        })}
      </div>
    </button>
  );
}

export function RitoBoardPage({
  onBack,
  onSelectCase,
}: {
  onBack: () => void;
  onSelectCase: (id: string) => void;
}): JSX.Element {
  const [board, setBoard] = useState<RitoBoard | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const resp = await apiFetch('/api/comite/rito-board');
      if (!resp.ok) {
        setError(`Falha ao carregar (${resp.status}).`);
        return;
      }
      setBoard((await resp.json()) as RitoBoard);
    } catch {
      setError('Falha de rede.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div style={{ maxWidth: 1200, margin: '24px auto', padding: '0 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="rito-back">
          ← Voltar
        </Button>
        <h2 style={{ margin: 0 }}>Rito — casos em curso</h2>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => void load()}
          loading={loading}
          data-testid="rito-refresh"
        >
          Atualizar
        </Button>
      </div>

      <p style={{ fontSize: 12, opacity: 0.65, marginBottom: 12 }}>
        Só metadados — categoria, estado, prazo e o que falta para avançar. Não mostra teor,
        conteúdo nem quem votou.
      </p>

      {error && (
        <Alert variant="error" style={{ marginBottom: 16 }}>
          {error}
        </Alert>
      )}

      {board && (
        <>
          <p style={{ opacity: 0.75, marginBottom: 12 }}>
            Total em curso: <strong data-testid="rito-total">{board.total}</strong>
          </p>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))',
              gap: 12,
              alignItems: 'start',
            }}
          >
            {board.stages.map((col) => (
              <Card key={col.stage} data-testid={`rito-col-${col.stage}`}>
                <h3 style={{ marginTop: 0, marginBottom: 8, fontSize: 15 }}>
                  {col.label}{' '}
                  <span style={{ opacity: 0.6, fontWeight: 400 }}>({col.cases.length})</span>
                </h3>
                {col.cases.length === 0 ? (
                  <p style={{ fontSize: 12, opacity: 0.5 }}>Nenhum caso.</p>
                ) : (
                  col.cases.map((card) => (
                    <CaseCardView key={card.caseId} card={card} onOpen={onSelectCase} />
                  ))
                )}
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
